/**
 * Staff first-time setup page.
 *
 * Reached from the invite email at /dashboard/setup?token=<raw_hex>.
 * GETs /api/auth/setup/check to validate the token + surface the email
 * it was issued for, then POSTs /api/auth/setup with the chosen
 * password. On success the user is redirected to /dashboard/login —
 * the backend does NOT issue a session here (no cookie set), which
 * forces them to re-enter the password they just chose. That's the
 * confirmation step that catches typos before TOTP enrolment.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { InlineAlert } from '../components/ui/States';
import { Wordmark } from '../components/Wordmark';
import { PasswordStrength, passwordMeetsPolicy } from '../components/PasswordStrength';
import { ApiResponseError, get, post } from '../lib/api';

const MIN_PASSWORD = 12;

type TokenState =
  | { state: 'loading' }
  | { state: 'ok'; email: string; expiresAt: string }
  | { state: 'expired' }
  | { state: 'invalid' };

export function DashboardSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [tokenState, setTokenState] = useState<TokenState>({ state: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    password?: string;
    confirm?: string;
  }>({});
  const [done, setDone] = useState(false);

  // Pre-flight token check on mount.
  useEffect(() => {
    if (!token) {
      setTokenState({ state: 'invalid' });
      return;
    }
    void (async () => {
      try {
        const r = await get<{ email: string; expires_at: string }>(
          `/auth/setup/check?token=${encodeURIComponent(token)}`,
        );
        setTokenState({ state: 'ok', email: r.email, expiresAt: r.expires_at });
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 401) {
          // Backend returns 401 for both expired and not-found; we
          // don't distinguish them in the UI (both → "request a fresh
          // invite") to avoid the enumeration oracle.
          setTokenState(
            err.message.toLowerCase().includes('expired')
              ? { state: 'expired' }
              : { state: 'invalid' },
          );
        } else {
          setTokenState({ state: 'invalid' });
        }
      }
    })();
  }, [token]);

  function validate(emailForPolicy: string): boolean {
    const errs: { password?: string; confirm?: string } = {};
    if (!password) errs.password = 'Password is required';
    else if (!passwordMeetsPolicy(password, MIN_PASSWORD, emailForPolicy)) {
      errs.password = 'Password does not meet the policy. See the checklist.';
    }
    if (!confirm) errs.confirm = 'Confirm your password';
    else if (password !== confirm) errs.confirm = 'Passwords do not match';
    setFieldError(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (tokenState.state !== 'ok' || !token) return;
    if (!validate(tokenState.email)) return;
    setIsLoading(true);
    try {
      await post('/auth/setup', { token, password });
      setDone(true);
      // 1.5s delay so the success card is visible before bouncing them
      // to login. Without this the redirect feels like an error
      // (form vanishes, login appears, no confirmation).
      setTimeout(() => navigate('/dashboard/login', { replace: true }), 1500);
    } catch (err) {
      const msg =
        err instanceof ApiResponseError ? err.message : 'Setup failed. Try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] px-6 py-16">
      <div className="w-full max-w-[460px]">
        <div className="mb-10 flex justify-center">
          <Wordmark />
        </div>

        {tokenState.state === 'loading' && (
          <p className="text-center font-sans text-[14px] text-[var(--color-ink-soft)]">
            Checking your invite…
          </p>
        )}

        {tokenState.state === 'invalid' && (
          <ErrorCard
            title="This Invite Link Is Invalid."
            body={
              <>
                The token is unrecognised. Ask an administrator to send
                you a fresh invite, then click the link in that email.
              </>
            }
          />
        )}

        {tokenState.state === 'expired' && (
          <ErrorCard
            title="This Invite Has Expired."
            body={
              <>
                Setup links expire after 7 days. Ask an administrator
                to issue a fresh invite.
              </>
            }
          />
        )}

        {tokenState.state === 'ok' && !done && (
          <>
            <p className="eyebrow mb-3">Set up your account</p>
            <h1 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
              Welcome to Prism.
            </h1>
            <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
              You've been invited to {tokenState.email}. Choose a
              password — you'll set up your authenticator app on the
              next screen.
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
              <Input
                label="Email"
                type="email"
                value={tokenState.email}
                disabled
                readOnly
              />
              <div>
                <Input
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={fieldError.password}
                  disabled={isLoading}
                  required
                  autoFocus
                />
                <PasswordStrength
                  value={password}
                  minLength={MIN_PASSWORD}
                  email={tokenState.email}
                  alwaysVisible
                />
              </div>
              <Input
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={fieldError.confirm}
                disabled={isLoading}
                required
              />
              {error && <InlineAlert variant="danger">{error}</InlineAlert>}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Set password
              </Button>
            </form>
          </>
        )}

        {done && (
          <div className="rounded-[6px] border border-[var(--color-success)]/40 bg-[var(--color-success-soft)] p-6">
            <p className="eyebrow mb-2 text-[var(--color-success-ink)]">
              All set
            </p>
            <p className="font-sans text-[14.5px] text-[var(--color-ink)]">
              Password saved. Redirecting you to sign in…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorCard({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="rounded-[6px] border border-[var(--color-rule)] bg-[var(--color-surface)] p-6">
      <p className="eyebrow mb-2">Action needed</p>
      <h2 className="text-[20px] font-semibold tracking-tight text-[var(--color-ink)]">
        {title}
      </h2>
      <p className="mt-3 font-sans text-[14px] leading-relaxed text-[var(--color-ink-soft)]">
        {body}
      </p>
    </div>
  );
}
